import React, { useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Toaster } from "react-hot-toast";
import { DataGrid } from "@mui/x-data-grid";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import SearchIcon from "@mui/icons-material/Search";
import exportExcel from "utils/excelGenerate";
import {
  getDealerChurn,
  getDealerChurnCount,
  getDealerChurnSalesData,
  getDealerChurnTimeline,
  getUsersList,
  submitChurnRemark,
} from "_controllers/officer";
import { toastMessage } from "utils/toastMessage";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import styles from "./dealerChurn.module.css";
import moment from "moment";
import Chart from "react-apexcharts";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import axios from "axios";
import { PageHeader, SectionCard, useChartBase, mergeChartOptions } from "_shared/ui";

// Side-panel modal surface — theme-aware so it reads correctly in dark mode.
const messageStyle = {
  position: "fixed",
  top: 0,
  right: 0,
  height: "100vh",
  width: "70%",
  bgcolor: "background.paper",
  border: "none",
  boxShadow: 24,
  pt: 2,
  pb: 0,
  pl: 4,
  pr: 4,
  textAlign: "left",
  letterSpacing: 1,
  overflowY: "auto",
};

// Maps a risk id / value to a theme semantic palette key.
const RISK_COLOR = {
  obviousChurner: "error",
  highRisk: "warning",
  lowRisk: "success",
};

const getRiskChipColor = (value) => {
  if (value === "High Risk") return "warning";
  if (value === "Obvious Churner") return "error";
  if (value === "Low Risk" || value === "No Risk" || value === "Performing Well")
    return "success";
  return "default";
};

const ActionItem = ({ data, index }) => (
  <Box
    sx={(theme) => ({
      position: "relative",
      color: "text.primary",
      p: 1.25,
      pl: 2.5,
      mb: 1.25,
      lineHeight: 1.4,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor:
        index === 0 ? alpha(theme.palette.primary.main, 0.08) : "transparent",
      "&::after": {
        content: '""',
        position: "absolute",
        left: -15,
        top: 0,
        bottom: -10,
        borderLeft: `2px dotted ${theme.palette.divider}`,
      },
      "&::before": {
        content: '""',
        position: "absolute",
        left: -15,
        top: 10,
        height: 2,
        width: 15,
        backgroundColor: theme.palette.divider,
      },
    })}
  >
    {console.log("index", data)}
    <Typography
      variant="caption"
      sx={{
        display: "block",
        mb: 0.5,
        color: index === 0 ? "success.main" : "text.secondary",
        fontWeight: index === 0 ? 700 : 400,
      }}
    >
      {moment
        .utc(data.onDate)
        .add(330, "minutes")
        .format("DD MMM YYYY hh:mm A")}
    </Typography>
    <Typography sx={{ color: "secondary.main", fontSize: 14, fontWeight: 600 }}>
      {data.remark}
    </Typography>
    {data?.actionRemark ? (
      <Typography sx={{ color: "text.primary", fontSize: 14, fontWeight: 600 }}>
        {data?.actionRemark}
      </Typography>
    ) : null}
    {data?.reasons && data?.reasons.length > 0 && (
      <Typography
        component="div"
        sx={{ color: "warning.main", fontSize: 14, fontWeight: 600 }}
      >
        Reason:{" "}
        {data?.reasons.map((reason, idx) => (
          <span key={idx}>
            {reason}
            {idx < data?.reasons.length - 1 && " | "}
          </span>
        ))}
      </Typography>
    )}
    {data?.suggestions && data.suggestions.length > 0 && (
      <Typography
        component="div"
        sx={{ color: "success.main", fontSize: 14, fontWeight: 600 }}
      >
        Suggested Actions:{" "}
        {data?.suggestions.map((suggestion, idx) => (
          <span key={idx}>
            {suggestion}
            {idx < data?.suggestions.length - 1 && " | "}
          </span>
        ))}
      </Typography>
    )}
    <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 600 }}>
      {data?.additionalRemark}
    </Typography>
  </Box>
);

const Main = () => {
  const theme = useTheme();
  const chartBase = useChartBase();
  const currentDate = new Date();
  const currentMonthNumber = currentDate.getMonth() + 1;

  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [churnCount, setChurnCount] = useState({});
  const [clubCategory, setClubCategory] = useState([]);
  const [dealerType, setDealerType] = useState([]);
  const [selectedClub, setSelectedClub] = useState("");
  const [selectedDealerType, setSelectedDealerType] = useState("");
  const [filteredData, setFilteredData] = useState(data);
  const [soResp, setSoResp] = useState({});
  const [month, setMonth] = useState(currentMonthNumber);
  const [year, setYear] = useState(2024);
  const [chartMonth, setChartMonth] = useState([]);
  const [chartSale, setChartSale] = useState([]);

  const [timelineData, setTimelineData] = useState();

  const [openModal, setOpenModal] = useState(false);
  const [comment, setComment] = useState("");

  const [riskType, setRiskType] = useState("");

  const [userList, setUserList] = useState([]);
  const [filteredUserList, setFilteredUserList] = useState([]);

  const [showMentionList, setShowMentionList] = useState(false);
  const [assignedTo, setAssignedTo] = useState("");

  const renderRiskLevelCell = (params) => {
    const cellValue = params.value;
    const chipColor = getRiskChipColor(cellValue);
    if (chipColor !== "default") {
      return <Chip size="small" label={cellValue} color={chipColor} />;
    }
    return cellValue;
  };

  const renderTooltipCell = (params) => {
    console.log("params", params);
  };

  const columns = useMemo(() => [
    {
      field: "dealerCode",
      headerName: "Dealer Code",
      flex: 2,
    },
    {
      field: "dealerName",
      headerName: "Dealer Name",
      flex: 4,
    },
    {
      field: "riskLevel",
      headerName: "Risk Level",
      flex: 3,
      renderCell: renderRiskLevelCell,
    },
    {
      field: "assigned",
      headerName: "Assigned To",
      flex: 2,
    },
    {
      field: "healthRemark",
      headerName: "Remarks",
      flex: 3,
      renderCell: renderRiskLevelCell,
    },
    {
      field: "status",
      headerName: "Action Plan Status",
      flex: 5,
    },
  ]);

  const handleChange = (e) => {
    setSearch(e.target.value);
  };

  useEffect(async () => {
    await getUsersList()
      .then((response) => {
        setUserList(response?.data);
      })
      .catch((err) => console.log("err", err));
  }, []);

  console.log("reposne", userList);

  useEffect(async () => {
    await getDealerChurnCount({ month, year })
      .then((d) => {
        // Defensive: the API response must carry `churnCount`/`soResponse`
        // arrays. If it doesn't (shape change, empty period, error payload),
        // fall back to [] so the reduce can't throw and silently blank the
        // cards. Log the shape so the real cause is visible in the console.
        if (!Array.isArray(d?.churnCount) || !Array.isArray(d?.soResponse)) {
          console.warn(
            "[DealerChurn] Unexpected churnCount response shape:",
            d
          );
        }
        const transformedObjectML = (d?.churnCount || []).reduce((acc, item) => {
          const { risk, current, percentageChange } = item;
          const riskKey = risk.toLowerCase().replace(/\s+/g, ""); // Convert to camelCase
          acc[riskKey] = current;
          acc[`changeIn${riskKey.charAt(0).toUpperCase()}${riskKey.slice(1)}`] =
            parseFloat(percentageChange);
          return acc;
        }, {});

        const finalObjectML = {
          lowRisk: transformedObjectML.lowrisk || 0,
          highRisk: transformedObjectML.highrisk || 0,
          obviousChurner: transformedObjectML.obviouschurner || 0,
          changeInLowRisk: transformedObjectML.changeInLowrisk || 0,
          changeInHighRisk: transformedObjectML.changeInHighrisk || 0,
          changeInObviousChurner:
            transformedObjectML.changeInObviouschurner || 0,
        };
        setChurnCount(finalObjectML);

        const transformedObject = (d?.soResponse || []).reduce((acc, item) => {
          const { risk, current, percentageChange } = item;
          const riskKey = risk.toLowerCase().replace(/\s+/g, "");
          acc[riskKey] = current;
          acc[`changeIn${riskKey.charAt(0).toUpperCase()}${riskKey.slice(1)}`] =
            parseFloat(percentageChange);
          return acc;
        }, {});

        const finalObject = {
          lowRisk: transformedObject.lowrisk || 0,
          highRisk: transformedObject.highrisk || 0,
          obviousChurner: transformedObject.obviouschurner || 0,
          changeInLowRisk: transformedObject.changeInLowrisk || 0,
          changeInHighRisk: transformedObject.changeInHighrisk || 0,
          changeInObviousChurner: transformedObject.changeInObviouschurner || 0,
        };
        setSoResp(finalObject);
        setLoading(false);
      })
      .catch((err) => {
        // Surface the real failure (auth, 5xx, network, bad shape) instead of
        // silently leaving the risk cards blank.
        console.error("[DealerChurn] Failed to load churn counts:", err);
        setLoading(false);
      });
  }, [month, year]);

  let newDate = new Date(new Date().getFullYear(), month - 1, 1);
  for (let i = 3; i >= 1; i--) {
    newDate.setDate(1);
    newDate.setMonth(newDate.getMonth() - 1);
    const lastMonthShortName = newDate.toLocaleString("default", {
      month: "short",
    });
    const year = newDate.getFullYear().toString().substr(-2);
    const headerName = `${lastMonthShortName}' ${year}`;
    const field = `month${i}`;
    columns.splice(2, 0, {
      field,
      headerName,
      flex: 2,
      renderCell: renderTooltipCell,
    });
  }

  useEffect(async () => {
    await getDealerChurn({ month, year })
      .then((d) => {
        const uniqueData = d?.data.reduce(
          (result, item) => {
            if (item.clubCategory && item.clubCategory !== "") {
              result.clubCategories.add(item.clubCategory);
            }
            if (item.dealerType1 && item.dealerType1 !== "") {
              result.dealerTypes.add(item.dealerType1);
            }
            result.test.push({
              id: v4(),
              dealerCode: item?.dealerCode,
              dealerName: item?.name,
              riskLevel: item?.MLResponse,
              remark: item?.remarks.length > 0 ? item.remarks[0] : "-",
              month1: item?.MLSalesData[2]?.saleValue,
              month2: item?.MLSalesData[1]?.saleValue,
              month3: item?.MLSalesData[0]?.saleValue,
              assigned: item?.SAP_ID,
              clubCategory: item?.clubCategory,
              dealerType1: item?.dealerType1,
              healthRemark: item?.healthRemark,
              status: item?.status,
            });
            return result;
          },
          {
            clubCategories: new Set(),
            dealerTypes: new Set(),
            test: [],
          }
        );

        setClubCategory(Array.from(uniqueData.clubCategories));
        setDealerType(Array.from(uniqueData.dealerTypes));
        setData(uniqueData.test);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toastMessage("error fetching data", "error");
      });
  }, [month, year]);

  useEffect(() => {
    const res = filteredData
      .filter(
        (data) =>
          data?.dealerCode?.includes(search) ||
          data?.dealerName?.toUpperCase().includes(search.toUpperCase()) ||
          data?.assigned?.toUpperCase().includes(search.toUpperCase())
      )
      .map((item) => ({
        id: item?.id,
        dealerCode: item?.dealerCode,
        dealerName: item?.dealerName,
        riskLevel: item?.riskLevel,
        remark: item?.remark?.length > 0 ? item.remark[0] : "-",
        month1: item?.month1,
        month2: item?.month2,
        month3: item?.month3,
        assigned: item?.assigned,
        clubCategory: item?.clubCategory,
        dealerType1: item?.dealerType1,
        healthRemark: item?.healthRemark,
        status: item?.status,
      }));
    setSearchData(res);
  }, [search]);

  const generateExcel = async (e) => {
    let fileName = "Churn-Report";
    e.preventDefault();
    exportExcel(
      filteredData.map((x) => {
        let firstMonth = moment
          .utc({ month: month - 1 })
          .subtract(2, "month")
          .format("MMMM");
        let secondMonth = moment
          .utc({ month: month - 1 })
          .subtract(1, "month")
          .format("MMMM");
        let thirdMonth = moment.utc({ month: month - 1 }).format("MMMM");

        return {
          "Dealer Code": x?.dealerCode,
          "Dealer Name": x?.dealerName,
          RiskLevel: x?.riskLevel,
          [firstMonth]: x?.month1,
          [secondMonth]: x?.month2,
          [thirdMonth]: x?.month3,
          Remark: x?.healthRemark ? x?.healthRemark : "-",
          Assigned: x?.assigned,
          Status: x?.status,
        };
      }),
      fileName
    );
  };

  let conflictData = [
    {
      label: "Obvious Churner",
      id: "obviousChurner",
      change: "changeInObviousChurner",
    },
    {
      label: "High Risk",
      id: "highRisk",
      change: "changeInHighRisk",
    },
    {
      label: "Low Risk",
      id: "lowRisk",
      change: "changeInLowRisk",
    },
  ];

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const years = [
    { value: 2022, label: 2022 },
    { value: 2023, label: 2023 },
    { value: 2024, label: 2024 },
  ];

  const clubNames = [
    { value: "GP", label: "Gold Plus" },
    { value: "BC", label: "Blue Club" },
    { value: "PL", label: "Platinum Club" },
    { value: "ST", label: "Starter Club" },
    { value: "AH", label: "Acer Club" },
    { value: "NS", label: "NON SAS" },
    { value: "DC", label: "Diamond Club" },
    { value: "CA", label: "Chairman Advisory" },
    { value: "CC", label: "Chairman Club" },
  ];

  useEffect(() => {
    let updatedData = data;
    if (selectedClub) {
      updatedData = updatedData.filter(
        (item) => item.clubCategory === selectedClub
      );
    }
    if (
      (selectedClub === "All" || selectedClub === "") &&
      selectedDealerType === ""
    ) {
      updatedData = data;
    }

    if (selectedDealerType) {
      updatedData = updatedData.filter(
        (item) => item.dealerType1 === selectedDealerType
      );
    }

    setFilteredData(updatedData);
  }, [selectedClub, selectedDealerType, data]);

  const handleConflict = (type) => {
    setRiskType(type);
    let riskLevel;

    switch (type) {
      case "obviousChurner":
        riskLevel = "Obvious Churner";
        break;
      case "highRisk":
        riskLevel = "High Risk";
        break;
      case "lowRisk":
        riskLevel = "Low Risk";
        break;
      default:
        console.log("Invalid type");
        return;
    }

    const filteredData = data.filter(
      (item) =>
        item.riskLevel === riskLevel &&
        item.healthRemark !== undefined &&
        item.healthRemark !== "" &&
        item.healthRemark !== item.riskLevel
    );

    setFilteredData(filteredData);
  };

  const handleReset = () => {
    setFilteredData(data);
    setRiskType("");
  };

  const handleDealer = async (data) => {
    setAssignedTo(data?.assigned);
    await getDealerChurnTimeline(data?.dealerCode)
      .then((res) => {
        console.log("res", res);
        setTimelineData(res.data);
        setOpenModal(true);
      })
      .catch((err) => {
        console.log("err", err);
      });

    await getDealerChurnSalesData(data?.dealerCode)
      .then((res) => {
        setChartMonth(res?.data?.map((item) => item.month).reverse());
        setChartSale(
          res?.data?.map((item) => item.SaleVal.toFixed()).reverse()
        );
      })
      .catch((err) => {
        console.log("err", err);
      });
  };

  const addComment = async (data) => {
    console.log("adat", timelineData);
    let remark = comment;
    await submitChurnRemark({ remark, dealerCode: data })
      .then((res) => {
        console.log("res", res);
        setTimelineData(res.data);
        setComment("");
        toastMessage(res.data.message, "success");
      })
      .catch((err) => {
        console.log("err", err);
        toastMessage("error adding comment", "error");
      });
  };

  let chartDataValue = {
    options: mergeChartOptions(chartBase, {
      labels: chartMonth,
      title: {
        text: "Sales Performance",
        align: "center",
      },
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        area: {
          fillTo: "origin",
        },
      },
      tooltip: { followCursor: true },
      markers: {
        size: 5,
      },
    }),

    series: [
      {
        name: "Sales Data",
        data: chartSale,
      },
    ],
  };

  const handleInputChange = (event) => {
    const text = event.target.value;
    const words = text.split(/\s+/);
    const lastWord = words[words.length - 1];
    const isAtMention = lastWord.startsWith("@") && lastWord.length > 1;

    setShowMentionList(isAtMention);
    const filterText = lastWord.substring(1).toLowerCase();
    const filteredMentions = userList.filter((mention) =>
      mention.toLowerCase().includes(filterText)
    );
    console.log("filter", filteredMentions);

    setFilteredUserList(filteredMentions);
    setComment(text);
  };

  const handleMentionSelect = (selectedMention) => {
    const updatedComment = comment.replace(/@(\S*)$/, `@${selectedMention} `);
    setComment(updatedComment);
    setShowMentionList(false);
  };

  const printPage = () => {
    window.print();
  };

  const formatNumber = (number) => {
    return String(number).replace(/(\d)(?=(\d{2})+\d$)/g, "$1,");
  };

  const mentionOptions =
    filteredUserList && filteredUserList.length > 0
      ? filteredUserList
      : userList;

  return (
    <>
      <PageHeader
        title="Dealer Churn Report"
        subtitle="Monitor churn risk and act on at-risk dealers"
        action={
          <Button
            variant="contained"
            startIcon={<CloudDownloadIcon />}
            onClick={generateExcel}
          >
            Download Report
          </Button>
        }
      />

      <Paper
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel id="zone-label">Filter by Club</InputLabel>
            <Select
              labelId="zone-label"
              value={selectedClub}
              name="zone"
              label="Filter by Club"
              onChange={(event) => setSelectedClub(event.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {clubNames.map(
                (club) =>
                  clubCategory.includes(club.value) && (
                    <MenuItem key={club.value} value={club.value}>
                      {club.label}
                    </MenuItem>
                  )
              )}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel id="month-label">Select Month</InputLabel>
            <Select
              labelId="month-label"
              value={month}
              name="zone"
              label="Select Month"
              onChange={(event) => setMonth(event.target.value)}
            >
              {months?.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel id="year-label">Select Year</InputLabel>
            <Select
              labelId="year-label"
              value={year}
              name="zone"
              label="Select Year"
              onChange={(event) => setYear(event.target.value)}
            >
              {years?.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <TextField
          size="small"
          onChange={handleChange}
          value={search}
          name="search"
          variant="outlined"
          label="Search Dealer Name or Code or Assigned"
          sx={{ minWidth: 340 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {conflictData && conflictData.length > 0
          ? conflictData.map((item, idx) => {
              const accent = RISK_COLOR[item?.id] || "primary";
              return (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Card sx={{ p: 2.5, height: "100%" }}>
                    <Typography
                      align="center"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: "0.5px",
                        color: `${accent}.main`,
                        mb: 1.5,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Analytics
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography
                            sx={{ color: `${accent}.main`, fontWeight: 600 }}
                          >
                            {churnCount[item?.id]}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={
                              churnCount[item?.change] > 0 ? (
                                <TrendingUpIcon />
                              ) : (
                                <TrendingDownIcon />
                              )
                            }
                            sx={{
                              color:
                                churnCount[item?.change] > 0
                                  ? "error.main"
                                  : "success.main",
                              fontWeight: 600,
                            }}
                          >
                            {churnCount[item?.change]}%
                          </Button>
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Ground Staff
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography
                            sx={{ color: `${accent}.main`, fontWeight: 600 }}
                          >
                            {soResp[item?.id]}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={
                              soResp[item?.change] > 0 ? (
                                <TrendingUpIcon />
                              ) : (
                                <TrendingDownIcon />
                              )
                            }
                            sx={{
                              color:
                                soResp[item?.change] > 0
                                  ? "error.main"
                                  : "success.main",
                              fontWeight: 600,
                            }}
                          >
                            {soResp[item?.change]}%
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography
                      align="center"
                      variant="body2"
                      sx={{
                        color: "secondary.main",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        if (riskType === item?.id && item?.id !== "Reset") {
                          // Handle the "Reset" case
                          handleReset(item?.id);
                        } else {
                          // Handle the "View Conflict" case
                          handleConflict(item?.id);
                        }
                      }}
                    >
                      {riskType === item?.id ? "Reset" : "View Conflict"}
                    </Typography>
                  </Card>
                </Grid>
              );
            })
          : null}
      </Grid>

      <SectionCard title="Dealer Churn" disablePadding>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={search ? searchData : filteredData}
            columns={columns}
            autoHeight={true}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            className="TableCell"
            rowsPerPageOptions={[10, 25, 50, 100]}
            onRowClick={(row) => handleDealer(row.row)}
            sx={{ fontSize: "0.75rem" }}
          />
        )}
      </SectionCard>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={messageStyle} className="modal-content">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2.5,
              mt: 0.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "secondary.main", fontWeight: 600 }}
            >
              {timelineData?.name}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                onClick={printPage}
                startIcon={<PictureAsPdfIcon />}
                sx={{ color: "text.secondary" }}
              >
                Download PDF
              </Button>
              <Button
                onClick={printPage}
                startIcon={<PrintIcon />}
                sx={{ color: "text.secondary" }}
              >
                Print
              </Button>
              <IconButton
                onClick={() => setOpenModal(false)}
                sx={{ color: "text.secondary" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ display: "flex" }}>
            <Box
              sx={{
                width: "70%",
                borderRight: `1px dotted ${theme.palette.divider}`,
                pr: 2,
              }}
            >
              <Chart
                type="area"
                width={"100%"}
                height={"250px"}
                series={chartDataValue.series}
                options={chartDataValue.options}
              />
            </Box>
            <Box
              sx={{
                width: "30%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                borderRight: `1px dotted ${theme.palette.divider}`,
                p: "10px 20px",
              }}
            >
              <Box sx={{ fontWeight: "bolder" }}>
                <Typography sx={{ color: "secondary.main" }}>
                  SAS{" "}
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    AOD
                  </Box>
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "text.primary", mt: 0.5 }}
                >
                  {formatNumber(parseInt(timelineData?.sasOut))}
                </Typography>
              </Box>
              <Box sx={{ my: 1.25, fontWeight: "bolder" }}>
                <Typography sx={{ color: "secondary.main" }}>
                  Outstanding
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1.25,
                  }}
                >
                  <Box sx={{ lineHeight: "20px" }}>
                    <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                      60 days
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", color: "text.primary" }}
                    >
                      {formatNumber(parseInt(timelineData?.out60))}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                      90 days
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", color: "text.primary" }}
                    >
                      {formatNumber(parseInt(timelineData?.outAbove90))}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ fontWeight: "bolder" }}>
                <Typography sx={{ color: "secondary.main" }}>Overdue</Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "text.primary" }}
                >
                  {formatNumber(parseInt(timelineData?.overdue))}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 4,
              fontSize: 14,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: "bold", color: "text.secondary" }}>
                Timeline
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "60%",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: "bold", color: "text.secondary" }}>
                  Action Plan{" "}
                  <Box
                    component="span"
                    sx={{ ml: 2, fontWeight: "bolder", color: "text.primary" }}
                  >
                    {"Executed by "}
                    {assignedTo}
                  </Box>
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: "bold", color: "text.secondary" }}>
                  Impact{" "}
                  <Box
                    component="span"
                    sx={{ ml: 2, fontWeight: "bolder", color: "text.primary" }}
                  >
                    Available in next cycle
                  </Box>
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              height: "20px",
              borderLeft: `2px dotted ${theme.palette.text.disabled}`,
              ml: "6px",
            }}
          />

          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                position: "relative",
              }}
            >
              <TextField
                size="small"
                fullWidth
                value={comment}
                placeholder="Write something..."
                onChange={handleInputChange}
              />
              {showMentionList && (
                <Paper
                  sx={{
                    position: "absolute",
                    zIndex: 1,
                    top: "100%",
                    left: 0,
                    width: "100%",
                    maxHeight: "150px",
                    overflowY: "auto",
                    my: 0.5,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box
                    component="ul"
                    sx={{ p: "0 10px", m: 0, listStyle: "none" }}
                  >
                    {mentionOptions.map((mention) => (
                      <Box
                        component="li"
                        key={mention}
                        onClick={() => handleMentionSelect(mention)}
                        sx={{
                          p: 1,
                          cursor: "pointer",
                          listStyle: "none",
                          fontWeight: "bold",
                          color: "text.secondary",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        {mention}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}
              <Button
                variant="contained"
                onClick={() => addComment(timelineData.dealerCode)}
              >
                Post
              </Button>
            </Box>
            <Box
              sx={{
                height: "40px",
                borderLeft: `2px dotted ${theme.palette.text.disabled}`,
                ml: "6px",
              }}
            >
              <Typography
                sx={{
                  ml: "2rem",
                  fontSize: 14,
                  mx: "20px",
                  pt: "10px",
                  color: "text.secondary",
                }}
              >
                Use{" "}
                <Box
                  component="span"
                  sx={{ color: "secondary.main", fontWeight: "bold" }}
                >
                  @ symbol
                </Box>{" "}
                on your keyboard to assign/escalate this to someone else or leave
                a remark
              </Typography>
            </Box>
          </Box>
          <div className={styles.timeline}>
            {timelineData?.remarks &&
              timelineData?.remarks.map((item, index) => (
                <ActionItem key={index} data={item} index={index} />
              ))}
          </div>
        </Box>
      </Modal>
    </>
  );
};

const DealerChurn = () => {
  return (
    <AccessController roles={["AO", "RO", "ZO", "ADMIN"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default DealerChurn;
